import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useParams, useLocation } from 'react-router';
import { toast } from 'react-toastify';
import {
    isEqual as _isEqual,
    cloneDeep as _cloneDeep,
    get as _get,
} from 'lodash';
import clsx from 'clsx';

import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Autocomplete from '@mui/material/Autocomplete';
import { DataGridPro, useGridApiRef as useApiRef } from '@mui/x-data-grid-pro';

import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

import Push from '../../../components/push/';
import Validator, { useValidate, checkValidity } from '../../../components/validator';
import FileUpload from '../../../components/file-upload';

import CountryCodes from '../../../data/country-codes';
import PolicyService from '../../../data/policy-service';
import Auth, { ProfileContext } from '../../../data/auth-service';

import useWindowSize from '../../../libraries/usewindowsize';
import Helpers, { SaveFile } from '../../../libraries/helpers';
import Animations from '../../../libraries/animations';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import CountryFlag from '../../../components/country-flag';

const useStyles = makeStyles(theme => ({
    formControl: {
        margin: theme.spacing(1),
        width: '100%',
    },
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(1),
    },
    closedAccordion: {
        display: 'none',
    },
    ...Animations.Push(theme),
    warningBox: {
        border: `1px solid ${theme.palette.warning.main}`,
        background: theme.styled.paper.background,
        padding: theme.spacing(2),
    },
}));

const empty = {
    address: '',
    geo: {
        city_name: '',
        country: 'AF',
        state: '',
        location: {
            lat: 0,
            lon: 0,
        }
    }
};

const columnDefaults = {
    sortable: true,
    type: 'string',
    disableColumnMenu: true,
    hidden: false,
};

const tableRenderers = {
    address: d => { return <div><CountryFlag country={d.row.geo.country} /> {d.row.address}</div> },
    country: d => { return <div>{d.row.geo.country}</div> },
    state: d => { return <div>{d.row.geo.state}</div> },
    city_name: d => { return <div>{d.row.geo.city_name}</div> },
    lat: d => { return <div>{d.row.geo.location.lat}</div> },
    lon: d => { return <div>{d.row.geo.location.lon}</div> },
};

const GeoIP = () => {
    const classes = useStyles();
    // const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();
    const [init, setInit] = useState(true);
    // const [data, setData] = useState([]);
    const [isEdit, setIsEdit] = useState(false);
    const [policy, _setPolicy] = useState({
        name: '',
        type: '',
        count: 0,
        data: { locations: [] },
    });
    const policyRef = useRef(policy);
    const [locationOpen, setLocationOpen] = useState(false);
    const [locData, setLocData] = useState(_cloneDeep(empty));
    // const [ogLocData, setOGLocData] = useState(_cloneDeep(empty));
    const [selected, setSelected] = useState(null);
    // const [tableHeight, setTableHeight] = useState(550);
    const [original, setOriginal] = useState();
    const [changes, setChanges] = useState();
    const geoForm = useValidate({});
    const [mode, setMode] = useState('geoip');
    const geoPerms = Auth.permissions.get('GeoIPPolicy');
    const [readonly, setReadonly] = useState(!geoPerms.write);
    const { height } = useWindowSize();
    const tableAPIRef = useApiRef();
    const [columnWidth, setColumnWidth] = useState({});
    const [columns, setColumns] = useState([]);
    const { updateProfile, getProfile } = useContext(ProfileContext);
    const [pushing, setPushing] = useState(false);

    const MC2CHECK = process.env.BRICATA_APPLIANCE_TYPE === 'mc2';

    const setPolicy = (state) => {
        policyRef.current = state;
        _setPolicy(state);
        setInit(true);
    };

    const mergePolicy = (data) => {
        const incoming = (data || [])
            .reduce((a, c) => ({ ...a, [c.address]: { ...c, id: Helpers.UniqueID() } }), []);

        _setPolicy(current => {
            const existing = current.data.locations
                .reduce((a, c) => ({ ...a, [c.address]: c }), []);
            current.data.locations = Object.values({ ...existing, ...incoming });
            toast(`${Object.keys(incoming).length} Addresses were added or updated`);
            policyRef.current = current;
            return { ...current };
        });
    };

    useEffect(() => {
        setChanges(!_isEqual(policy, original));
        setInit(true);
    }, [policy, original]);

    const getPolicy = useCallback(() => {
        PolicyService.getPolicy('custom_geoip', params.policy).then(res => {
            if (!res.data.locations) {
                res.data.locations = [];
            }
            res.data.locations = res.data.locations.map((v, i) => {
                v.id = Helpers.UniqueID();
                return v;
            });
            setOriginal(_cloneDeep(res));
            setPolicy(res);
            setReadonly(current =>
                current || (geoPerms.override ? false : (!MC2CHECK && (res.source === 'mc2' || location.pathname.includes('.mc2')))));
        }, err => {
            if (typeof err === 'string') {
                toast.error(err);
            }
        });
    }, [MC2CHECK, location.pathname, params.policy]);

    useEffect(() => {
        getPolicy();

        setColumnWidth(getProfile('geoip').widths || {});
    }, [getPolicy, getProfile]);

    useEffect(() => {
        setColumns([
            {
                ...columnDefaults,
                width: columnWidth['IP Address/Range'] || 0,
                flex: columnWidth['IP Address/Range'] ? undefined : 1,
                headerName: 'IP Address/Range',
                field: 'address',
                renderCell: tableRenderers.address,
                valueGetter: (p) => { return p.row.address }
            },
            {
                ...columnDefaults,
                width: columnWidth.Country || 0,
                flex: columnWidth.Country ? undefined : 1,
                headerName: 'Country',
                field: 'geo.country',
                renderCell: tableRenderers.country,
                valueGetter: (v) => { return v.row.geo.country }
            },
            {
                ...columnDefaults,
                width: columnWidth.State || 0,
                flex: columnWidth.State ? undefined : 1,
                headerName: 'State',
                field: 'geo.state',
                renderCell: tableRenderers.state,
                valueGetter: (v) => { return v.row.geo.state }
            },
            {
                ...columnDefaults,
                width: columnWidth.City || 0,
                flex: columnWidth.City ? undefined : 1,
                headerName: 'City',
                field: 'geo.city_name',
                renderCell: tableRenderers.city_name,
                valueGetter: (v) => { return v.row.geo.city_name }
            },
            {
                ...columnDefaults,
                width: columnWidth.Latitude || 0,
                flex: columnWidth.Latitude ? undefined : 1,
                headerName: 'Latitude',
                field: 'geo.location.lat',
                renderCell: tableRenderers.lat,
                valueGetter: (v) => { return v.row.geo.location.lat }
            },
            {
                ...columnDefaults,
                width: columnWidth.Longitude || 0,
                flex: columnWidth.Longitude ? undefined : 1,
                headerName: 'Longitude',
                field: 'geo.location.lon',
                renderCell: tableRenderers.lon,
                valueGetter: (v) => { return v.row.geo.location.lon }
            },
        ]);
    }, [columnWidth]);

    const handlePushPolicy = () => {
        setMode('push');
        setInit(true);
    };

    const handleDeployPolicy = (sensorList) => {
        setPushing(true);
        PolicyService.updatePolicy('custom_geoip', params.policy, policy).then(res => {
            setMode('geoip');
            toast.success('Policy saved successfully');
            getPolicy();

            if (sensorList.length || Object.keys(sensorList).length) {
                PolicyService.assignPolicy('custom_geoip', params.policy, sensorList).then(res => {
                    toast.success('Policy pushed to new sensor(s) successfully');
                }, err => {
                    if (typeof err === 'string') {
                        toast.error('Pushing policy to new sensor(s) failed : ' + err);
                    } else {
                        toast.error(err.message + ', ' + err.error);
                    }
                });
            }
        }, err => {
            if (err) {
                toast.error(err.message + ': ' + err.error);
            }
        }).finally(() => { setPushing(false) });
    };

    const handleRevertPolicy = () => {
        getPolicy();
        setIsEdit(false);
        setLocData(_cloneDeep(empty));
        setLocationOpen(false);
    };

    const handleImport = data => {
        // verify data is array of objects
        if (!Array.isArray(data)) {
            toast.error('File must be a JSON array of GeoIP mappings');
            return;
        }

        mergePolicy(data);
    };

    const handleExport = () => {
        try {
            const data = JSON.stringify(_get(policy, 'data.locations', []));
            SaveFile(data, `${policy.name}_geoip.json`, 'text/plain');
        } catch (err) {
            toast.error(`Failed to export GeoIP file: ${err && err.message}`);
            console.error(err);
        }
    };

    const handleAddLocation = () => {
        setIsEdit(false);
        setLocData(_cloneDeep(empty));
        setLocationOpen(!locationOpen);
    };

    const handleAddressUpdate = (e, validID, valid) => {
        geoForm.set({ ...geoForm.get(), [validID]: valid });

        setLocData({
            ...locData,
            address: e.target.value,
        });
    };

    const handleCountryUpdate = (e, country) => {
        const l = { ...locData };
        l.geo.country = country.code;
        setLocData(l);
    };

    const handleGeoUpdate = (e) => {
        const l = { ...locData };
        l.geo[e.target.name] = e.target.value;
        setLocData(l);
    };

    const handleLocUpdate = (e, validID, valid) => {
        geoForm.set({ ...geoForm.get(), [validID]: valid });

        const l = { ...locData };
        l.geo.location[e.target.name] = e.target.value === '-' || e.target.value === '' ? e.target.value : parseFloat(e.target.value);
        setLocData(l);
    };

    const handleCreateLoc = () => {
        const p = { ...policy };
        if (isEdit) {
            const i = p.data.locations.findIndex(v => v.id === locData.id);
            p.data.locations[i] = locData;
        } else {
            p.data.locations.push({
                ...locData,
                id: Helpers.UniqueID(),
            });
        }

        setInit(false);
        setPolicy(p);
        handleAddLocation();
    };

    const handleCancelLoc = () => {
        setLocData(_cloneDeep(empty));
        setIsEdit(false);
        handleAddLocation();
    };

    const handleSelect = (s) => {
        setSelected(s.row);
    };

    const handleColumnResize = params => {
        const widths = tableAPIRef.current.getAllColumns().reduce((a, c) => {
            a[c.headerName] = c.width;
            return a;
        }, {});
        updateProfile('geoip', {
            ...getProfile('geoip'),
            widths,
        });
        setColumnWidth(current => ({
            ...current,
            [_get(params, 'colDef.headerName', 'unknown')]: params.width,
        }));
    };

    const handleColumnReorder = () => {
        let order = [];
        setTimeout(() => {
            order = tableAPIRef.current.getAllColumns().reduce((a, c) => {
                a.push(c.headerName);
                return a;
            }, []);

            updateProfile('geoip', {
                ...getProfile('geoip'),
                columns: order,
            });
        });
    };

    const handleDeleteLoc = () => {
        setInit(false);
        const p = { ...policy };
        p.data.locations.splice(p.data.locations.findIndex(v => v.id === selected.id), 1);
        setPolicy(p);
        setSelected(null);
    };

    const handleEditLoc = () => {
        setIsEdit(true);
        setLocData(_cloneDeep(selected));
        setLocationOpen(true);
    };

    const handleModeChange = (m) => {
        setMode(m);
    };

    return (
        <main>
            <Grid container spacing={2}>
                <Grid container item xs={12} style={{ display: mode === 'push' ? 'none' : 'flex' }}>
                    <Grid container item xs={12} className={classes.justifySpace}>
                        <Grid item xs={12} display="flex" justifyContent="space-between">
                            <Box>
                                {!MC2CHECK && policy.source === 'mc2' && <Box className={classes.warningBox} display="inline-block">
                                    You cannot modify or push a policy assigned by an MC2.
                                </Box>}
                            </Box>
                            {!readonly && <Box display="flex">
                                {/* import */}
                                <FileUpload
                                    id="assets-import-file"
                                    onImport={handleImport}
                                    component={
                                        <Button
                                            className={classes.buttonRight}
                                            variant="contained"
                                            color="primary"
                                            component="span"
                                            aria-label="import assets">
                                            <ImportExportIcon/>{'Import'}
                                        </Button>
                                    }/>
                                {/* export */}
                                <Box ml={1}><Button variant="contained" color="primary" aria-label="export assets" onClick={handleExport}><ImportExportIcon />Export</Button></Box>
                                <Box ml={1}><Button
                                    variant="contained"
                                    ml={1}
                                    aria-label="add location"
                                    onClick={handleAddLocation}
                                    disabled={!MC2CHECK && policy.source === 'mc2'}
                                ><AddIcon/> Location</Button></Box>
                                {geoPerms.admin && <Box ml={1}><Button
                                    variant="contained"
                                    color="primary"
                                    aria-label="push"
                                    onClick={handlePushPolicy}
                                    disabled={!MC2CHECK && policy.source === 'mc2'}
                                    className={clsx({
                                        [classes.pushAnimation]: !(!MC2CHECK && policy.source === 'mc2') && changes,
                                    })}
                                >Push</Button></Box>}
                                <Box ml={1}><Button
                                    variant="contained"
                                    color="secondary"
                                    aria-label="revert"
                                    onClick={handleRevertPolicy}
                                    disabled={!MC2CHECK && policy.source === 'mc2'}
                                >Revert</Button></Box>
                            </Box>}
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} style={{ display: mode === 'push' ? '' : 'none' }}>
                    {geoPerms.admin && <Push
                        policyName={params.policy}
                        policyType="custom_geoip"
                        onDeploy={handleDeployPolicy}
                        closePush={() => { handleModeChange('geoip') }}
                        pushing={pushing}
                        permName={'GeoIPPolicy'}
                    />}
                </Grid>
                <Grid item xs={12} style={{ display: mode === 'geoip' ? '' : 'none' }}>
                    <Box mb={locationOpen ? 2 : 0}>
                        <Accordion expanded={locationOpen}>
                            <AccordionSummary className={classes.closedAccordion}></AccordionSummary>
                            <AccordionDetails>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <IconButton
                                            className={classes.buttonRight}
                                            aria-label="close location"
                                            onClick={handleAddLocation}
                                        ><CloseIcon /></IconButton>
                                    </Grid>
                                    <Grid item xs={12} container spacing={2}>
                                        <Grid item xs={6}>
                                            <Validator
                                                valueType="ipRange"
                                                errorText="IP is invalid"
                                                fullWidth
                                                label="IP/Range"
                                                aria-label="ip/range"
                                                autoCorrect="off"
                                                autoComplete="off"
                                                onChange={handleAddressUpdate}
                                                value={locData.address}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Autocomplete
                                                options={Object.keys(CountryCodes).map((v) => {
                                                    return { country: CountryCodes[v][0], code: v };
                                                })}
                                                getOptionLabel={(l) => {
                                                    return l.country || CountryCodes[l][0] || '';
                                                }}
                                                style={{ width: '100%' }}
                                                renderInput={(params) => <TextField {...params} label="Country" />}
                                                value={locData.geo.country}
                                                onChange={handleCountryUpdate}
                                                isOptionEqualToValue={(option, value) => _get(option, 'code') === value}
                                                clearIcon={null}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12} container spacing={2}>
                                        <Grid item xs={6}>
                                            <TextField
                                                fullWidth
                                                label="State"
                                                aria-label="state"
                                                autoCorrect="off"
                                                autoComplete="off"
                                                name="state"
                                                onChange={handleGeoUpdate}
                                                value={locData.geo.state}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                fullWidth
                                                label="City"
                                                aria-label="city_name"
                                                autoCorrect="off"
                                                autoComplete="off"
                                                name="city_name"
                                                onChange={handleGeoUpdate}
                                                value={locData.geo.city_name}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12} container spacing={2}>
                                        <Grid item xs={6}>
                                            <Validator
                                                valueType="range"
                                                errorText="Latitude must be within -90 and 90"
                                                type="number"
                                                fullWidth
                                                inputProps={{
                                                    min: -90,
                                                    max: 90,
                                                }}
                                                label="Lat"
                                                aria-label="lat"
                                                autoCorrect="off"
                                                autoComplete="off"
                                                name="lat"
                                                onChange={handleLocUpdate}
                                                value={locData.geo.location.lat}
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Validator
                                                valueType="range"
                                                errorText="Longitude must be within -180 and 180"
                                                type="number"
                                                inputProps={{
                                                    min: -180,
                                                    max: 180,
                                                }}
                                                fullWidth
                                                label="Lon"
                                                name="lon"
                                                aria-label="lon"
                                                autoCorrect="off"
                                                autoComplete="off"
                                                onChange={handleLocUpdate}
                                                value={locData.geo.location.lon}
                                                min={-180}
                                                max={180}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Box my={2}>
                                            <Button
                                                className={classes.buttonRight}
                                                variant="contained"
                                                color="primary"
                                                aria-label="add"
                                                onClick={handleCreateLoc}
                                                disabled={!checkValidity(geoForm.get())}
                                            >
                                                {isEdit ? 'Update' : 'Add'}
                                            </Button>
                                            <Button className={classes.buttonRight} variant="contained" color="secondary" aria-label="cancel" onClick={handleCancelLoc}>Cancel</Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    </Box>
                </Grid>
            </Grid>
            <Box height={height - 220} display={mode === 'geoip' ? 'flex' : 'none'}>
                {init && <DataGridPro
                    apiRef={tableAPIRef}
                    components={{
                        Header: readonly ? undefined : () => {
                            return (
                                <Box p={1}>
                                    <Grid container>
                                        <Grid item xs={12}>
                                            <IconButton
                                                disabled={!selected}
                                                className={classes.buttonRight}
                                                variant="contained"
                                                color="primary"
                                                aria-label="edit selected ip"
                                                onClick={handleEditLoc}
                                            ><EditIcon /></IconButton>
                                            <IconButton
                                                disabled={!selected}
                                                className={classes.buttonRight}
                                                variant="contained"
                                                color="secondary"
                                                aria-label="delete selected ip"
                                                onClick={handleDeleteLoc}
                                            ><DeleteIcon /></IconButton>
                                        </Grid>
                                    </Grid>
                                </Box>
                            );
                        },
                    }}
                    onRowClick={handleSelect}
                    disableMultipleSelection
                    disableColumnFilter
                    disableColumnMenu
                    onColumnResize={handleColumnResize}
                    onColumnOrderChange={handleColumnReorder}
                    columns={columns}
                    rows={(() => {
                        // Why this is necessary, I have no idea...
                        const l = [...policyRef.current.data.locations];
                        return l;
                    })()}
                />}
            </Box>
        </main>
    );
};

export default GeoIP;
